import { useEffect, useState } from 'react'
import { IconButton, Progress, Tooltip, Typography } from '@material-tailwind/react'
import { IoMdPlay, IoMdPause, IoMdFastforward, IoMdRewind, IoMdClose } from 'react-icons/io'
import { PiInfoDuotone } from 'react-icons/pi'
import { ActiveAudio } from '../types/geoXp'

const formatTimer = (t: number) => {
  if (t > 0) {
    const o = new Date(0)
    const p = new Date(t * 1000)
    const isoString = new Date(p.getTime() - o.getTime()).toISOString()
    const timePart = isoString.split('T')[1]?.split('Z')[0]?.split(':')
    if (timePart && timePart.length >= 3) {
      return {
        mm: timePart[1] || '00',
        ss: timePart[2]?.split('.')[0] || '00',
      }
    }
  }
  return { mm: '00', ss: '00' }
}

interface PlayerProps {
  spot: ActiveAudio
}

export default function Player({ spot }: PlayerProps) {
  const [progress, setProgress] = useState(0)

  const audio = spot?.audio
  // eslint-disable-next-line no-underscore-dangle
  const duration = (audio as { _duration?: number })?._duration || 0
  const isPlaying = Boolean(audio?.playing?.())
  const total = formatTimer(duration)
  const elapsed = formatTimer(progress)

  useEffect(() => {
    const seek = setInterval(() => {
      try {
        const p = audio?.seek()
        setProgress(p)
      } catch {
        // do nothing
      }
    }, 100)
    return () => clearInterval(seek)
  }, [audio])

  const stop = () => {
    if (audio) audio.stop()
  }

  const skip = (s: number) => {
    const p = progress + s
    if (p > 0 && p < duration) audio?.seek(p)
  }

  return (
    <div className="bg-white dark:bg-primary-500 px-4 md:px-6 py-4">
      <div className="flex items-center justify-between">
        <div>
          {spot?.spot?.description ? (
            <div className="flex items-center justify-start gap-3">
              <Typography className="font-bold">{spot.spot.label}</Typography>
              <Tooltip content={<div className="w-[300px]">{spot?.spot?.description}</div>}>
                <span>
                  <PiInfoDuotone />
                </span>
              </Tooltip>
            </div>
          ) : (
            <Typography className="font-bold">{spot.spot.label}</Typography>
          )}

          {spot?.spot?.creator && (
            <Typography variant="small">
              Created by {spot.spot.creator?.name} {spot.spot.creator?.surname}
            </Typography>
          )}
        </div>
        <IconButton onClick={stop} size="sm" variant="text" data-testid="player-close-btn">
          <IoMdClose className="text-xl text-primary dark:text-white" />
        </IconButton>
      </div>
      <div className="flex items-center w-full gap-x-3">
        <IconButton variant="text" size="sm" onClick={() => skip(-10)}>
          <IoMdRewind className="text-xl text-primary dark:text-white mx-2" />
        </IconButton>
        <IconButton
          variant="text"
          size="sm"
          onClick={() => (audio?.playing() ? audio.pause() : audio.play())}
          data-testid="player-toggle-btn"
          data-playing={isPlaying ? 'true' : 'false'}
        >
          {isPlaying ? (
            <IoMdPause className="text-xl text-primary dark:text-white" />
          ) : (
            <IoMdPlay className="text-xl text-primary dark:text-white" />
          )}
        </IconButton>
        <IconButton variant="text" size="sm" onClick={() => skip(10)}>
          <IoMdFastforward className="text-xl text-primary dark:text-white" />
        </IconButton>
        <Typography variant="small">
          {elapsed.mm}:{elapsed.ss}
        </Typography>
        <Progress
          value={Math.floor((progress / duration) * 100)}
          className="bg-gray-200 dark:bg-primary-200/50"
          barProps={{ className: 'bg-black dark:bg-white' }}
        />
        <Typography variant="small">
          {total.mm}:{total.ss}
        </Typography>
      </div>
    </div>
  )
}
